import React, {useState, useMemo, useContext} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import styled from 'styled-components';
import {isEmpty, isEqual} from 'lodash';
import {labels} from '../../../../../../../util/utils';
import {ListHeaderColumnTypes, guidanceType, AccountType} from '../../../../../../../util/uiconstants';
import {HelixIntegrationContext} from '../../../../../../Main/Ares/Helix/HelixIntegrationModal/index';
import ListHeader from '../../../../../../Main/Ares/Common/ListHeader/ListHeader';
import ClientDataModal from '../Common/ClientDataModal/clientDataModal';
import {
	displayTooltip,
	displayDate,
	displayRiskPill,
	renderExecutionType,
	getAccountDesignation,
	convertToDecimal
} from '../AccountOverview/index';
import {displayValue} from '../AccountOverview/indexDisplayValue';
import {displayAnalyticsIcon} from '../AccountOverview/indexDisplayAnalyticsIcon';
import {useUserPreferences, useBlackListAnalyzerTypes} from '../../../../../../../util/customHooks';
import {IconoirArrowUp, IconoirArrowDown} from '@ey-xd/motif-icon';

export default function ViewAndEditAccount({engagement, account, trialBalance, selectedEntityId, materiality, body}) {
	const useBlackListAnalyzerTypesValue = useBlackListAnalyzerTypes();
	const userPreferences = useUserPreferences();
	const {setShowModal, setModalProps} = useContext(HelixIntegrationContext);

	const [showClientDataModal, setShowClientDataModal] = useState(false);

	const hasHelixProjects = useSelector((store) => store.configurations?.features?.hasHelixProjects || false);
	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);

	const hasHelixComponentGuidance =
		body?.guidances?.find((g) => g.guidanceTypeId === guidanceType.HelixComponent) !== undefined;

	const helixConnection = engagementHelixProjects.find((x) => x.isPrimary);

	const guidances = body?.guidances
		?.filter((g) => g.guidanceTypeId === guidanceType.HelixComponent)
		.sort((a, b) => a.guidanceDisplayOrder - b.guidanceDisplayOrder)
		.slice(0, 6);

	const hideHelixComponentColumn = useMemo(() => {
		const {isMestEngagement} = engagement || {};
		const noSelectedEntity = isEqual(selectedEntityId, 0);
		const noTrialBalance = isEmpty(trialBalance);
		const noFinancialStatements = isEmpty(trialBalance?.financialStatements);

		return (isMestEngagement && noSelectedEntity) || noTrialBalance || noFinancialStatements;
	}, [engagement, selectedEntityId, trialBalance]);

	const showIcon = useMemo(() => {
		const hasSelectedEntity = !isEqual(selectedEntityId, 0);
		const {isMestEngagement} = engagement || {};
		const {isPrimary} = helixConnection || {};

		return (
			(isMestEngagement ? hasSelectedEntity : true) &&
			trialBalance &&
			isPrimary &&
			hasHelixComponentGuidance &&
			!useBlackListAnalyzerTypesValue
		);
	}, [helixConnection, engagement, hasHelixComponentGuidance, selectedEntityId, trialBalance]);

	const displayedValues = (value, financialStatement) => {
		switch (value) {
			case 'Analytics': {
				const val = convertToDecimal(financialStatement?.analysisPeriodBalance, false, true);
				return displayValue(
					val,
					true,
					false,
					false,
					undefined,
					1,
					onMultipleClick,
					false,
					false,
					userPreferences,
					IconoirArrowUp,
					IconoirArrowDown
				);
			}
			case 'Change': {
				const val = convertToDecimal(
					financialStatement?.analysisComparativeBalanceChangePercentage,
					false,
					true,
					IconoirArrowUp,
					IconoirArrowDown
				);
				return displayValue(
					val,
					false,
					false,
					true,
					'%',
					1,
					undefined,
					false,
					false,
					userPreferences,
					IconoirArrowUp,
					IconoirArrowDown
				);
			}
			case 'ComparativePeriod': {
				const val = convertToDecimal(financialStatement?.comparativePeriodBalance, true);
				return displayValue(
					val,
					true,
					false,
					false,
					undefined,
					1,
					onMultipleClick,
					false,
					false,
					userPreferences,
					IconoirArrowUp,
					IconoirArrowDown
				);
			}
			case 'xTE': {
				const xTE =
					materiality?.teAmount && materiality?.teAmount > 0
						? convertToDecimal(financialStatement?.analysisPeriodBalance / materiality?.teAmount, false, false, 1)
						: undefined;

				let abs = Math.abs(xTE);
				if (abs > 9999.99) {
					abs = 9999.99;
				}

				return displayValue(
					abs,
					false,
					true,
					false,
					'x',
					2,
					undefined,
					undefined,
					undefined,
					userPreferences,
					IconoirArrowUp,
					IconoirArrowDown
				);
			}
		}
	};

	const renderValues = (value) => {
		if (trialBalance && trialBalance.financialStatements && trialBalance.financialStatements.length > 1) {
			return displayTooltip(value, onMultipleClick);
		} else if (trialBalance && trialBalance.financialStatements && trialBalance.financialStatements.length === 1) {
			return trialBalance.financialStatements.map((financialStatement) => {
				return displayedValues(value, financialStatement);
			});
		} else {
			return <span className="noDataDash">{'-'}</span>;
		}
	};

	const firstColumnsConfig = [
		{
			id: 'desigantion',
			type: ListHeaderColumnTypes.custom,
			title: labels.designation,
			className: 'styledDesignation',
			render: () => getAccountDesignation(account.accountTypeId)
		},
		{
			id: 'risk',
			type: ListHeaderColumnTypes.custom,
			title: labels.risksLabel,
			className: 'styledRisk',
			render: () => displayRiskPill(account.assertions)
		},
		{
			id: 'estimate',
			type: ListHeaderColumnTypes.label,
			title: labels.estimate,
			className: 'styledEstimate',
			value: account.hasEstimate ? labels.yes : labels.no
		},
		{
			id: 'analyticsIcon',
			type: ListHeaderColumnTypes.icon,
			title: showIcon ? labels.analytics : '',
			className: 'styledAnalyticsIcon',
			renderIcon: () => (showIcon ? displayAnalyticsIcon(showIcon, onAnalyticsIconClick, hasHelixProjects) : undefined)
		}
	];

	// Do not show estimate if the account designation is limited risk or insignificant
	if (account.accountTypeId === AccountType.LimitedRisk || account.accountTypeId === AccountType.InSignificant) {
		const index = firstColumnsConfig.findIndex((col) => col.id === 'estimate');

		firstColumnsConfig.splice(index, 1);
	}

	// Do not show analytics icon if the conditions are not met
	if (!showIcon) {
		const index = firstColumnsConfig.findIndex((col) => col.id === 'analyticsIcon');

		firstColumnsConfig.splice(index, 1);
	}

	const secondColumnsConfig = [
		{
			id: 'analytics',
			type: ListHeaderColumnTypes.custom,
			title: labels.analysisPeriodTitle,
			date: displayDate(trialBalance?.analysisPeriodDate, userPreferences),
			className: 'styledAnalytics',
			render: () => renderValues('Analytics')
		},
		{
			id: 'comparativePeriod',
			type: ListHeaderColumnTypes.custom,
			title: labels.comparativePeriodTitle,
			date: displayDate(trialBalance?.comparativePeriodDate, userPreferences),
			className: 'styledComparativePeriod',
			render: () => renderValues('ComparativePeriod')
		},
		{
			id: 'change',
			type: ListHeaderColumnTypes.custom,
			title: labels.analysisPeriodChangeTitle,
			date: displayDate(trialBalance?.comparativePeriodDate, userPreferences),
			className: 'styledChange',
			render: () => renderValues('Change')
		},
		{
			id: 'xTE',
			type: ListHeaderColumnTypes.custom,
			title: labels.xTELabel,
			className: 'styledXTe',
			render: () => renderValues('xTE')
		}
	];

	const onMultipleClick = () => {
		setShowClientDataModal(true);
	};

	const onAnalyticsIconClick = () => {
		if (hasHelixProjects) {
			const accountId = account.id;
			setModalProps({accountId, guidances});
			setShowModal(true);
		}
	};

	return (
		<StyledViewAndEditAccount className="StyledViewAndEditAccount">
			<section className="headersList">
				<ListHeader
					name={account.accountName}
					columns={firstColumnsConfig}
					secondColumns={!hideHelixComponentColumn ? secondColumnsConfig : undefined}
					headerClassName={'styledAccountName'}
					headerChildren={renderExecutionType(account.executionTypeId)}
					mutliRows
				/>
				{showClientDataModal && (
					<ClientDataModal
						account={account}
						stEntityId={selectedEntityId}
						showModal={showClientDataModal}
						setShowModal={setShowClientDataModal}
					/>
				)}
			</section>
		</StyledViewAndEditAccount>
	);
}

const StyledViewAndEditAccount = styled.section`
	margin-bottom: var(--px-24);
	&:last-child {
		margin-bottom: 0;
	}
	.tooltiplabel {
		display: inline-flex;
		&.showTxt {
			cursor: pointer;
			font-size: var(--px-14);
			font-weight: 700;
			line-height: var(--px-22);
			color: var(--blue-600);
		}
	}
	.DisplayAmount {
		display: inline-flex;
		&.showTxt {
			cursor: pointer;
			font-size: var(--px-14);
			font-weight: 700;
			line-height: var(--px-22);
			color: var(--blue-600);
		}
	}
	.executionTypeLabel {
		margin-left: var(--px-16);
		border: var(--px-1) solid var(--neutrals-900);
		border-radius: var(--px-12);
		padding: var(--px-10);
		height: var(--px-24);
		display: inline-flex;
		align-items: center;
		width: auto;
		max-width: var(--px-200);
		cursor: default;
		user-select: none;
		.executionTypeText {
			font-size: var(--px-12);
			font-weight: 400;
			line-height: var(--px-16);
			color: var(--neutrals-900);
		}
	}
	.RiskPillsContainer {
		display: inline-flex;
		margin-right: var(--px-24);
		height: var(--px-24);
		align-items: center;
		.motif-dropdown-portal {
			display: inline-flex;
			.motif-dropdown-trigger {
				display: inline-flex;
			}
		}
		svg {
			color: var(--neutrals-800);
			height: inherit;
		}
	}
`;
